import { Icon } from '../icons';
import { landing } from '../../content/landing';
import { Reveal } from './Reveal';

/**
 * Khối "ba điều bot khác không làm được" — dùng chung cho trang giới thiệu và
 * trang /features, vì đây là đoạn bán hàng quan trọng nhất và hai chỗ lệch nhau
 * một chữ là mất luôn tác dụng.
 *
 * Chữ nằm ở `content/landing.ts` (`landing.differentiators`), không có chuỗi
 * nào khai ở đây — giống mọi trang public khác.
 *
 * Đánh số 01/02/03 thay vì gắn icon: ngay bên dưới khối này là lưới 12 thẻ tính
 * năng, thẻ nào cũng có icon. Thêm icon ở đây thì khối này trông như một hàng
 * nữa của lưới đó, đúng thứ cần tránh.
 */
export function Differentiators(): React.JSX.Element {
  const { eyebrow, title, strapline, items, loop } = landing.differentiators;

  return (
    <div className="rounded-2xl border border-accent-edge bg-accent-soft p-5 text-accent-soft-fg sm:p-8">
      {/*
        Eyebrow đổi sang mono: cùng bộ JetBrains Mono panel đã nhúng sẵn — nhãn
        nhỏ chữ hoa dạng terminal là chất "kỹ thuật" duy nhất trang này cần,
        không phải thêm font mới.
      */}
      <p className="font-mono text-xs font-semibold tracking-widest uppercase opacity-80">{eyebrow}</p>
      <h2 className="mt-1.5 text-2xl font-bold tracking-tight">{title}</h2>
      <p className="mt-2 max-w-3xl text-sm leading-relaxed opacity-90">{strapline}</p>

      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        {items.map((item, index) => (
          <Reveal key={item.title} delay={index * 120} className="h-full">
          <div
            className="flex h-full flex-col rounded-xl border border-border bg-surface p-5 text-fg shadow-sm"
          >
            <span className="font-mono text-sm font-bold tabular-nums text-accent">
              {String(index + 1).padStart(2, '0')}
            </span>
            <h3 className="mt-1 text-base font-semibold">{item.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-fg-muted">{item.body}</p>
            {/*
              Câu đối chiếu đẩy xuống ĐÁY thẻ (`mt-auto`) để ba thẻ có độ dài
              thân khác nhau vẫn thẳng hàng nhau ở phần gạch chân.
            */}
            <p className="mt-auto border-t border-border pt-3 text-xs leading-relaxed text-fg-subtle">
              {item.contrast}
            </p>
          </div>
          </Reveal>
        ))}
      </div>

      <Reveal className="mt-4">
      <div className="rounded-xl border border-border bg-surface p-5 text-fg shadow-sm">
        <h3 className="text-sm font-semibold">{loop.title}</h3>
        <ol className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {loop.steps.map((step, index) => (
            <li key={step.label} className="relative rounded-lg bg-surface-2 p-3 pr-4">
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold tabular-nums text-accent">{index + 1}</span>
                <span className="text-sm font-semibold">{step.label}</span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-fg-muted">{step.detail}</p>
              {/*
                Mũi tên nối các bước, chỉ vẽ ở bề ngang đủ rộng để bốn bước nằm
                một hàng. Xếp dọc thì mũi tên chỉ sang phải là chỉ vào khoảng
                trống, nên ẩn hẳn.
              */}
              {index < loop.steps.length - 1 && (
                <span
                  aria-hidden="true"
                  className="absolute top-1/2 -right-2.5 hidden -translate-y-1/2 text-fg-subtle lg:block"
                >
                  <Icon name="caret-right" size={14} />
                </span>
              )}
            </li>
          ))}
        </ol>
        <p className="mt-3 text-xs leading-relaxed text-fg-subtle">{loop.footnote}</p>
      </div>
      </Reveal>
    </div>
  );
}
